import { useEffect } from "react";
import { useParams } from "react-router";
import { styled } from "styled-components";

import { triggerLogin } from "authentication/actions";
import { Spinner } from "common/components";
import { Status } from "common/types/status";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useGo } from "common/utilities/useGo";
import { getIsAuthenticated } from "domains/authentication/selectors";
import { clearSingle, fetchCollectionForEditing, fetchCollections } from "domains/collection/actions";
import { CreateOrEditCollection } from "domains/collection/components/edit/CreateOrEditCollection";
import { createIsCollectionClientSideSelector, getCollectionForEditing } from "domains/collection/selectors";
import { CollectionType } from "domains/collection/types/collectionType";
import { getSiteLanguage } from "domains/language/selectors";
import { activateModal } from "domains/modal/actions";
import { getCanEditCollections } from "entities/permissions/selectors";

const StyledSpinner = styled(Spinner)`
  color: ${({ theme }) => theme.colors.textBright};
  display: block;
  margin: 3rem auto;
`;

export const ProtectedEditCollectionPage = () => {
  const dispatch = useDispatch();
  const go = useGo();
  const { collectionId: id } = useParams();
  const canEditCollections = useSelector(getCanEditCollections);
  const forEditing = useSelector(getCollectionForEditing);
  const isAuthenticated = useSelector(getIsAuthenticated);
  const siteLanguage = useSelector(getSiteLanguage);

  if (id === undefined) throw new Error("collectionId param undefined in EditCollection.");

  const isClientSide = useSelector(createIsCollectionClientSideSelector(id));

  useEffect(() => {
    dispatch(isClientSide ? fetchCollections(CollectionType.Editable) : fetchCollectionForEditing(id));
  }, [dispatch, id, isClientSide, siteLanguage]);

  useEffect(() => {
    if (!isAuthenticated) {
      dispatch(triggerLogin({ onDismissRoute: "/" }));
      return;
    }

    if (!canEditCollections || (typeof forEditing !== "string" && !forEditing.canEdit)) {
      go(`/collection/${id}`, { replace: true });
      return;
    }

    if (forEditing === Status.Error) {
      dispatch(clearSingle());
      dispatch(
        activateModal({ id: "Error", params: { localisedMessageKey: "app.collection.unavailable.error_message" } }),
      );
      go("/create?mode=collection", { replace: true });
    }
  }, [canEditCollections, dispatch, forEditing, go, id, isAuthenticated]);

  const isLoading = typeof forEditing === "string" || !forEditing.canEdit || isClientSide;
  return !isAuthenticated ? null : isLoading ? <StyledSpinner /> : <CreateOrEditCollection collection={forEditing} />;
};
